import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  bulkVerifyChannels,
  bulkUpdateChannelStatus,
  bulkUpdateChannelFriendLimit,
} from 'api/line';
import { queryKeys } from 'constants/queryKeys';
import { lineStrings } from 'constants/strings';
import { useAuth } from 'contexts/AuthContext';
import type { ELineChannelStatus, TBulkChannelOperationResponse } from 'types';
import { toErrorMessage } from 'hooks/utils';

type UseBulkChannelOperationsOptions = {
  onVerifySuccess?: (response: TBulkChannelOperationResponse) => void;
  onUpdateStatusSuccess?: (response: TBulkChannelOperationResponse) => void;
  onUpdateFriendLimitSuccess?: (response: TBulkChannelOperationResponse) => void;
  onError?: () => void;
};

type UseBulkChannelOperationsResult = {
  verify: (lineChannelIds: number[]) => void;
  updateStatus: (lineChannelIds: number[], status: ELineChannelStatus) => void;
  updateFriendLimit: (lineChannelIds: number[], friendLimit: number) => void;
  isVerifying: boolean;
  isUpdatingStatus: boolean;
  isUpdatingFriendLimit: boolean;
  isProcessing: boolean;
  error: string | undefined;
};

export function useBulkChannelOperations(
  options: UseBulkChannelOperationsOptions = {},
): UseBulkChannelOperationsResult {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const strings = lineStrings.management;

  const invalidateAccounts = () => {
    queryClient.invalidateQueries({
      queryKey: [queryKeys.LINE_CHANNEL_ACCOUNTS, user?.uid],
    });
  };

  const verifyMutation = useMutation({
    mutationFn: async (lineChannelIds: number[]) => {
      if (!user) {
        throw new Error(strings.errors.unauthorized);
      }
      return bulkVerifyChannels(lineChannelIds);
    },
    onSuccess: (response) => {
      invalidateAccounts();
      options.onVerifySuccess?.(response);
    },
    onError: () => {
      options.onError?.();
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async (params: {
      lineChannelIds: number[];
      status: ELineChannelStatus;
    }) => {
      if (!user) {
        throw new Error(strings.errors.unauthorized);
      }
      return bulkUpdateChannelStatus(params.lineChannelIds, params.status);
    },
    onSuccess: (response) => {
      invalidateAccounts();
      options.onUpdateStatusSuccess?.(response);
    },
    onError: () => {
      options.onError?.();
    },
  });

  const updateFriendLimitMutation = useMutation({
    mutationFn: async (params: {
      lineChannelIds: number[];
      friendLimit: number;
    }) => {
      if (!user) {
        throw new Error(strings.errors.unauthorized);
      }
      return bulkUpdateChannelFriendLimit(
        params.lineChannelIds,
        params.friendLimit,
      );
    },
    onSuccess: (response) => {
      invalidateAccounts();
      options.onUpdateFriendLimitSuccess?.(response);
    },
    onError: () => {
      options.onError?.();
    },
  });

  // 別操作のエラーが残って alertMessage に混ざらないよう、新しい操作の開始時に全 mutation の状態をクリアする
  const resetMutations = () => {
    verifyMutation.reset();
    updateStatusMutation.reset();
    updateFriendLimitMutation.reset();
  };

  const verify = (lineChannelIds: number[]) => {
    resetMutations();
    verifyMutation.mutate(lineChannelIds);
  };

  const updateStatus = (
    lineChannelIds: number[],
    status: ELineChannelStatus,
  ) => {
    resetMutations();
    updateStatusMutation.mutate({ lineChannelIds, status });
  };

  const updateFriendLimit = (
    lineChannelIds: number[],
    friendLimit: number,
  ) => {
    resetMutations();
    updateFriendLimitMutation.mutate({ lineChannelIds, friendLimit });
  };

  const mutationError =
    verifyMutation.error ??
    updateStatusMutation.error ??
    updateFriendLimitMutation.error;
  const error = mutationError
    ? toErrorMessage(mutationError, strings.bulk.errors.operationFailed)
    : undefined;

  return {
    verify,
    updateStatus,
    updateFriendLimit,
    isVerifying: verifyMutation.isPending,
    isUpdatingStatus: updateStatusMutation.isPending,
    isUpdatingFriendLimit: updateFriendLimitMutation.isPending,
    isProcessing:
      verifyMutation.isPending ||
      updateStatusMutation.isPending ||
      updateFriendLimitMutation.isPending,
    error,
  };
}
